import { RouteInfoContext } from "@/components/context-providers/RouteInfoProvider";
import { useScopedNavLinkEnvironmentId } from "@/components/navigation/nav-link-environment";
import { type FlagCheckerV2, useFlagsV2 } from "@/utils/hooks/use-flags-grpc";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { SessionContext } from "next-auth/react";
import React, { createContext, useCallback, useContext, useEffect } from "react";

/** Which navigation to show: the environment flag's choice, or one forced in this browser. */
export type CategoryNavMode = "flag" | "classic" | "category";

interface CategoryNavOverrideState extends Record<string, unknown> {
  mode?: CategoryNavMode;
}

const NO_OVERRIDE_STATE: CategoryNavOverrideState = {};
// Stands in where next-auth is mocked without its context, as some unit tests do.
const NO_SESSION_CONTEXT = createContext<React.ContextType<typeof SessionContext>>(undefined);
const CATEGORY_NAV_MODES: readonly unknown[] = ["flag", "classic", "category"];

/**
 * The signed-in session. Read from the session's context rather than `useSession`, which throws
 * outside a provider: these gates also run where there is none, such as unit tests and
 * stickersheets, which read as signed in as no employee.
 */
const useSessionContext = () => useContext(SessionContext ?? NO_SESSION_CONTEXT);

/** Whether the signed-in user is a Chalk employee, admin mode or not. */
const useSignedInChalkEmployee = () =>
  useSessionContext()?.data?.user?.email?.endsWith("@chalk.ai") === true;

/**
 * Whether the reader may arrange the category nav: drag its rail icons, sections, and rows, rename,
 * add, and delete sections, and pin rows to the rail. Only Chalk employees may; everyone else sees
 * the default arrangement, whatever this browser has stored.
 */
export const useCanArrangeCategoryNav = useSignedInChalkEmployee;

interface CategoryNavArrangesState extends Record<string, unknown> {
  arranges?: boolean;
}

const UNKNOWN_ARRANGES_STATE: CategoryNavArrangesState = {};

/**
 * Whether the nav shows this browser's stored arrangement rather than the default one: the
 * reader's, once the session says who they are. The session loads after the first render, so
 * until then this follows the answer the last session gave, and an employee's arrangement does
 * not give way to the defaults on every load. Writing stays with `useCanArrangeCategoryNav`.
 */
export const useShowsArrangedCategoryNav = (): boolean => {
  const sessionLoading = useSessionContext()?.status === "loading";
  const canArrange = useCanArrangeCategoryNav();
  const [last, setLast] = useLocalStorageSyncedState<CategoryNavArrangesState>(
    LocalStorageKey.CATEGORY_NAV_ARRANGES,
    UNKNOWN_ARRANGES_STATE,
  );
  useEffect(() => {
    if (!sessionLoading && last.arranges !== canArrange) setLast({ arranges: canArrange });
  }, [canArrange, last.arranges, sessionLoading, setLast]);
  return sessionLoading ? last.arranges === true : canArrange;
};

/**
 * The per-browser navigation choice Chalk admins make from the top rail's switch, which applies
 * in every environment. Until a browser makes one, Chalk employees get the category nav and
 * everyone else follows the flag. Any choice is then kept, Flag included, so an employee who
 * picks Classic or Flag stays there.
 */
export const useCategoryNavOverride = (): readonly [
  CategoryNavMode,
  (mode: CategoryNavMode) => void,
] => {
  const [state, setState] = useLocalStorageSyncedState<CategoryNavOverrideState>(
    LocalStorageKey.CATEGORY_NAV_OVERRIDE,
    NO_OVERRIDE_STATE,
  );
  const isChalkEmployee = useSignedInChalkEmployee();
  const setMode = useCallback((mode: CategoryNavMode) => setState({ mode }), [setState]);
  const chosen = CATEGORY_NAV_MODES.includes(state.mode) ? state.mode : undefined;
  return [chosen ?? (isChalkEmployee ? "category" : "flag"), setMode] as const;
};

/**
 * The live flags of the environment a global settings page's nav points into. That page's own
 * flags are the team's, so `ScopedEnvironmentFlagsProvider` fetches these for the gate below.
 */
export interface ScopedEnvironmentFlags {
  environmentId: string;
  flags: FlagCheckerV2;
}

export const ScopedEnvironmentFlagsContext = createContext<ScopedEnvironmentFlags | undefined>(
  undefined,
);

/** Whether the category nav is on, and whether that answer is final. */
export interface CategoryNavState {
  enabled: boolean;
  /** False while the flags that decide are loading; `enabled` reads false until then. */
  ready: boolean;
}

const PENDING: CategoryNavState = { enabled: false, ready: false };
const fromFlags = (flags: FlagCheckerV2): CategoryNavState =>
  flags.ready ? { enabled: flags.enabled("onboard_experience"), ready: true } : PENDING;

/**
 * Whether this page uses the category-rail navigation, the one gate every surface that differs
 * between the two navigations reads.
 *
 * A navigation chosen in this browser, or a Chalk employee's default of the category nav
 * (`useCategoryNavOverride`), decides at once. Otherwise the `onboard_experience` flag decides, and
 * until it has loaded the answer is not `ready`: the nav shows a placeholder rather than guessing,
 * so it never paints one navigation and then swaps to the other, and nothing remembered in the
 * browser can stand in for the flag.
 *
 * A global settings page's nav follows an environment its URL does not name (`scopedEnvironmentId`,
 * or the one a `NavLinkEnvironmentProvider` above names); that environment's live flags decide
 * there. With no such environment, the team's flags do.
 */
export const useCategoryNavState = (options?: {
  scopedEnvironmentId?: string;
}): CategoryNavState => {
  const flags = useFlagsV2();
  // Read optionally: hosts without route info (unit tests, some stickersheets) follow the flags.
  const routeInfo = useContext(RouteInfoContext);
  const providedScopedEnvironmentId = useScopedNavLinkEnvironmentId();
  const scopedEnvironmentId = options?.scopedEnvironmentId ?? providedScopedEnvironmentId;
  const scopedFlags = useContext(ScopedEnvironmentFlagsContext);
  const [overrideMode] = useCategoryNavOverride();

  if (overrideMode !== "flag") return { enabled: overrideMode === "category", ready: true };
  // Until the route resolves, an environment page cannot yet be told from a global one.
  if (routeInfo != null && !routeInfo.isReady) return PENDING;
  if (routeInfo?.environmentId == null && scopedEnvironmentId != null) {
    // Flags fetched for another environment do not answer for this one.
    return scopedFlags?.environmentId === scopedEnvironmentId
      ? fromFlags(scopedFlags.flags)
      : PENDING;
  }
  return fromFlags(flags);
};

/** Whether the category nav is on; false until that is known (see `useCategoryNavState`). */
export const useCategoryNavEnabled = (options?: { scopedEnvironmentId?: string }): boolean =>
  useCategoryNavState(options).enabled;

/**
 * Whether `useCategoryNavEnabled` has its final answer. Pages that exist only in the category nav
 * wait for this before sending anyone away.
 */
export const useCategoryNavSettled = (): boolean => useCategoryNavState().ready;
